import * as v from 'valibot';
import { ProjectTypeSchema } from '@fe-learner-common/api/schemas/sessionsSchema';

export const SelfEnrollmentStatus = v.fallback(
  v.union([
    v.literal('active'), // user is enrolled
    v.literal('unenrolled'), // user is not enrolled
    v.literal('enrolled_confirmed'), // user is enrolled, awaiting to be confirmed
    v.literal('unknown'), // fallback case - not a valid state from the backend.
  ]),
  'unknown',
);

export const SelfEnrollmentProgress = v.union([
  v.literal('incomplete'),
  v.literal('unknown'),
  v.literal('completed'),
  v.literal('notAttempted'),
]);

export const PersonSchema = v.object({
  id: v.number(),
  name: v.string(),
  thumbnail: v.nullish(v.string()),
});

export const WaitlistSchema = v.object({
  seatNumber: v.nullish(v.number()),
  seatsAvailable: v.boolean(),
  displayDetails: v.boolean(),
  enabled: v.boolean(),
});

export const EnrollmentSchema = v.object({
  current: v.object({
    limit: v.nullish(v.number()),
    show: v.boolean(),
  }),
  maximum: v.object({
    limit: v.nullish(v.number()),
    show: v.boolean(),
  }),
  to: v.nullish(v.number()),
  from: v.nullish(v.number()),
  cancellable: v.boolean(),
  enabled: v.boolean(),
  mode: v.string(),
});

const DisEnrollmentSchema = v.object({
  to: v.nullish(v.number()),
  from: v.nullish(v.number()),
  enabled: v.boolean(),
  mode: v.string(),
});

const VisibilitySchema = v.object({
  visibility: v.string(),
  show: v.boolean(),
  to: v.nullish(v.number()),
  from: v.nullish(v.number()),
});

export const DatesSchema = v.object({
  displayTimezone: v.nullish(v.string()),
  ends: v.nullish(v.string()),
  id: v.string(),
  locationUrl: v.nullish(v.string()),
  place: v.nullish(
    v.object({
      googleMapsUrl: v.nullish(v.string()),
      location: v.nullish(v.string()),
      name: v.nullish(v.string()),
    }),
  ),
  starts: v.nullish(v.string()),
  subType: v.nullish(v.string()),
  timezone: v.nullish(v.string()),
  title: v.nullish(v.string()),
  type: v.nullish(v.string()),
});

export const ParticipantInfoSchema = v.object({
  showThumbnail: v.boolean(),
  showName: v.boolean(),
  enabled: v.boolean(),
});

export const SessionSchema = v.object({
  id: v.number(),
  uuid: v.string(),

  bgImage: v.nullish(v.string()),
  selfEnrollmentStatus: v.nullish(SelfEnrollmentStatus),
  selfEnrollmentProgress: v.nullish(SelfEnrollmentProgress),
  selfEnrollmentParticipationUrl: v.nullish(v.string()),
  description: v.nullish(v.string()),
  timezone: v.nullish(v.string()),
  startDate: v.nullish(v.string()),
  endDate: v.nullish(v.string()),
  bgColor: v.nullish(v.string()),
  name: v.nullish(v.string()),
  awardsAvailable: v.boolean(),
  certificatesAvailable: v.boolean(),
  diplomasAvailable: v.boolean(),
  location: v.nullish(v.string()),
  language: v.nullish(v.string()),
  duration: v.nullish(v.number()),
  locale: v.nullish(v.string()),
  format: v.nullish(v.string()),
  version: v.number(),
  managed: v.boolean(),
  categories: v.array(v.number()),
  anonymousAccess: v.boolean(),

  trainers: v.nullish(v.array(PersonSchema)),
  participants: v.nullish(v.array(PersonSchema)),
  waitlist: WaitlistSchema,
  enrollment: EnrollmentSchema,
  disenrollment: DisEnrollmentSchema,
  visibility: VisibilitySchema,
  dates: v.nullish(v.array(DatesSchema)),

  participantInfo: v.nullish(ParticipantInfoSchema),
  projectType: v.nullish(ProjectTypeSchema),
});

export type TSessionDetails = v.Output<typeof SessionSchema>;
export type TPerson = v.Output<typeof PersonSchema>;
export type TSelfEnrollmentStatus = v.Output<typeof SelfEnrollmentStatus>;
export type TSelfEnrollmentProgress = v.Output<typeof SelfEnrollmentProgress>;
export type TDate = v.Output<typeof DatesSchema>;
export type TEnrollment = v.Output<typeof EnrollmentSchema>;
